import { CARBON_QUOTES_SVG } from "site/icons/carbon-quotes";

export const RULE_NATIVE_COLOR_CLASSES = ["red-coral"] as const;
export type RuleNativeColorClass = (typeof RULE_NATIVE_COLOR_CLASSES)[number];

export const RULE_NATIVE_STYLE_CLASSES = ["ar", ...RULE_NATIVE_COLOR_CLASSES] as const;

const STYLE_ONLY_HEADS = new Set<string>(["ar", ...RULE_NATIVE_COLOR_CLASSES]);
const KNOWN_BLOCK_TAGS = new Set(["p"]);
const COLOR_CLASS_SET = new Set<string>(RULE_NATIVE_COLOR_CLASSES);
const STYLE_CLASS_SET = new Set<string>(RULE_NATIVE_STYLE_CLASSES);
const VAR_NAME_RE = /^[a-zA-Z_][a-zA-Z0-9_]*$/;
const VAR_TOKEN_RE = /^\[\[([a-zA-Z_][a-zA-Z0-9_]*)\]\]/;

function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

function classAttr(classes: string[]): string {
  const unique = [...new Set(classes.filter(Boolean))];
  return unique.length ? ` class="${unique.join(" ")}"` : "";
}

type TagHead = {
  tag: string;
  classes: string[];
};

type CharColorOptions = {
  first?: { n: number; className: string };
  last?: { n: number; className: string };
  /** 1-based inclusive ranges and single indices. */
  ranges?: Array<{ from: number; to: number; className: string }>;
};

type BraceToken = {
  kind: "tag" | "range";
  tag: string;
  classes: string[];
  colorOpts: CharColorOptions;
  body: string;
  length: number;
};

function filterStyleClasses(classes: string[]): string[] {
  return classes.filter((cls) => STYLE_CLASS_SET.has(cls));
}

function normalizeTagHead(raw: string): TagHead | null {
  const head = raw.trim();
  if (!head) {
    return null;
  }
  const parts = head.split(".").filter(Boolean);
  if (!parts.length) {
    return null;
  }
  const first = parts[0]!;
  if (STYLE_ONLY_HEADS.has(first) && parts.length === 1) {
    return { tag: "span", classes: filterStyleClasses([first]) };
  }
  if (first === "p" && parts.length === 1) {
    return { tag: "p", classes: [] };
  }
  return null;
}

export function isRuleVarName(name: string): boolean {
  return VAR_NAME_RE.test(name);
}

function parseRangeHead(raw: string): CharColorOptions | null {
  const head = raw.trim();
  const firstMatch = head.match(/^first(\d+)\.([a-z0-9-]+)$/i);
  if (firstMatch) {
    const className = resolveColorClass(firstMatch[2]!);
    if (!className) {
      return null;
    }
    return { first: { n: Math.max(0, Number.parseInt(firstMatch[1]!, 10)), className } };
  }

  const lastMatch = head.match(/^last(\d+)\.([a-z0-9-]+)$/i);
  if (lastMatch) {
    const className = resolveColorClass(lastMatch[2]!);
    if (!className) {
      return null;
    }
    return { last: { n: Math.max(0, Number.parseInt(lastMatch[1]!, 10)), className } };
  }

  const rangeMatch = head.match(/^(\d+)(?:-(\d+))?\.([a-z0-9-]+)$/);
  if (rangeMatch) {
    const className = resolveColorClass(rangeMatch[3]!);
    if (!className) {
      return null;
    }
    const from = Math.max(1, Number.parseInt(rangeMatch[1]!, 10));
    const to = rangeMatch[2] ? Math.max(from, Number.parseInt(rangeMatch[2]!, 10)) : from;
    return { ranges: [{ from, to, className }] };
  }

  return null;
}

function openHead(tag: string, classes: string[]): string {
  if (tag === "ar" || (tag === "span" && classes.length === 1 && classes[0] === "ar")) {
    return "ar";
  }
  if (classes.length === 1 && STYLE_CLASS_SET.has(classes[0]!)) {
    return classes[0]!;
  }
  return classes.length ? `${tag}.${classes.join(".")}` : tag;
}

export function formatBraceTag(tag: string, classes: string[], inner: string): string {
  return `{${openHead(tag, classes)}:${inner}}`;
}

export function formatVarTag(name: string): string {
  return `[[${name}]]`;
}

function resolveColorClass(raw: string): string | null {
  const value = raw.trim().toLowerCase();
  return value && COLOR_CLASS_SET.has(value) ? value : null;
}

function mergeColorOpts(a: CharColorOptions, b: CharColorOptions): CharColorOptions {
  return {
    first: b.first ?? a.first,
    last: b.last ?? a.last,
    ranges: [...(a.ranges ?? []), ...(b.ranges ?? [])],
  };
}

function colorizeText(value: string, opts: CharColorOptions): string {
  const chars = [...value];
  if (!chars.length) {
    return "";
  }

  const classes: Array<string | undefined> = new Array(chars.length).fill(undefined);

  if (opts.ranges) {
    for (const range of opts.ranges) {
      const from = Math.max(1, range.from) - 1;
      const to = Math.min(chars.length, range.to);
      for (let i = from; i < to; i += 1) {
        classes[i] = range.className;
      }
    }
  }

  if (opts.first?.n) {
    for (let i = 0; i < Math.min(opts.first.n, chars.length); i += 1) {
      classes[i] = opts.first.className;
    }
  }

  if (opts.last?.n) {
    for (let i = Math.max(0, chars.length - opts.last.n); i < chars.length; i += 1) {
      classes[i] = opts.last.className;
    }
  }

  const out: string[] = [];
  let index = 0;
  while (index < chars.length) {
    const className = classes[index];
    let next = index + 1;
    while (next < chars.length && classes[next] === className) {
      next += 1;
    }
    const text = escapeHtml(chars.slice(index, next).join(""));
    if (className) {
      out.push(`<span class="${className}">${text}</span>`);
    } else {
      out.push(text);
    }
    index = next;
  }

  return out.join("");
}

function readBodyUntilCloseBrace(rest: string, bodyStart: number): { body: string; totalLength: number } | null {
  let depth = 1;
  for (let i = bodyStart; i < rest.length; i += 1) {
    const ch = rest[i];
    if (ch === "{") {
      depth += 1;
    } else if (ch === "}") {
      depth -= 1;
      if (depth === 0) {
        return { body: rest.slice(bodyStart, i), totalLength: i + 1 };
      }
    }
  }
  return null;
}

const HEAD_RE =
  /^(first\d+(?:\.[a-z][a-z0-9-]*)+|last\d+(?:\.[a-z][a-z0-9-]*)+|\d+(?:-\d+)?(?:\.[a-z][a-z0-9-]*)+|[a-z][a-z0-9-]*)/i;

function matchBraceTag(rest: string): BraceToken | null {
  if (!rest.startsWith("{")) {
    return null;
  }
  const headMatch = rest.slice(1).match(HEAD_RE);
  if (!headMatch) {
    return null;
  }
  const head = headMatch[1]!;
  let cursor = 1 + headMatch[0].length;
  if (rest[cursor] !== ":") {
    return null;
  }
  cursor += 1;

  const wrapped = readBodyUntilCloseBrace(rest, cursor);
  if (!wrapped) {
    return null;
  }

  const rangeOpts = parseRangeHead(head);
  if (rangeOpts) {
    return {
      kind: "range",
      tag: "span",
      classes: [],
      colorOpts: rangeOpts,
      body: wrapped.body,
      length: wrapped.totalLength,
    };
  }

  const parsed = normalizeTagHead(head);
  if (!parsed) {
    return null;
  }

  return {
    kind: "tag",
    tag: parsed.tag,
    classes: parsed.classes,
    colorOpts: {},
    body: wrapped.body,
    length: wrapped.totalLength,
  };
}

type RowCell = { kind: "normal"; text: string } | { kind: "group"; text: string };

function splitSegmentIntoNormalCells(segment: string, cells: RowCell[]): void {
  let depth = 0;
  let start = 0;

  for (let i = 0; i < segment.length; i += 1) {
    const ch = segment[i];
    if (ch === "{") {
      depth += 1;
    } else if (ch === "}") {
      depth -= 1;
    } else if (ch === "|" && depth === 0) {
      const part = segment.slice(start, i).trim();
      if (part) {
        cells.push({ kind: "normal", text: part });
      }
      start = i + 1;
    }
  }

  const part = segment.slice(start).trim();
  if (part) {
    cells.push({ kind: "normal", text: part });
  }
}

function parseRowLine(line: string): RowCell[] | null {
  const cells: RowCell[] = [];
  let index = 0;

  while (index < line.length) {
    let depth = 0;
    let groupStart = -1;
    for (let i = index; i < line.length - 1; i += 1) {
      const ch = line[i];
      if (ch === "{") {
        depth += 1;
      } else if (ch === "}") {
        depth -= 1;
      } else if (ch === ":" && line[i + 1] === ":" && depth === 0) {
        groupStart = i;
        break;
      }
    }

    if (groupStart < 0) {
      splitSegmentIntoNormalCells(line.slice(index), cells);
      break;
    }

    splitSegmentIntoNormalCells(line.slice(index, groupStart), cells);

    depth = 0;
    let groupEnd = -1;
    for (let i = groupStart + 2; i < line.length - 1; i += 1) {
      const ch = line[i];
      if (ch === "{") {
        depth += 1;
      } else if (ch === "}") {
        depth -= 1;
      } else if (ch === ":" && line[i + 1] === ":" && depth === 0) {
        groupEnd = i;
        break;
      }
    }

    if (groupEnd < 0) {
      splitSegmentIntoNormalCells(line.slice(groupStart), cells);
      break;
    }

    const inner = line.slice(groupStart + 2, groupEnd).trim();
    if (inner) {
      cells.push({ kind: "group", text: inner });
    }
    index = groupEnd + 2;
  }

  if (cells.length < 2 || cells.some((cell) => !cell.text.trim())) {
    return null;
  }
  return cells;
}

function splitIntoColumns(text: string): string[] | null {
  const cells = parseRowLine(text);
  if (!cells) {
    return null;
  }
  return cells.map((cell) => cell.text);
}

function splitByColumnSeparator(text: string): string[] {
  const columns: string[] = [];
  let depth = 0;
  let start = 0;

  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    if (ch === "{") {
      depth += 1;
    } else if (ch === "}") {
      depth -= 1;
    } else if (ch === "|" && depth === 0) {
      columns.push(text.slice(start, i).trim());
      start = i + 1;
    }
  }

  columns.push(text.slice(start).trim());
  return columns.filter(Boolean);
}

function unwrapGroupedColumn(colText: string): { grouped: boolean; inner: string } {
  const trimmed = colText.trim();
  if (!trimmed.startsWith("::") || !trimmed.endsWith("::") || trimmed.length < 4) {
    return { grouped: false, inner: colText };
  }
  return { grouped: true, inner: trimmed.slice(2, -2).trim() };
}

function rowGridTemplateColumns(count: number): string {
  if (count <= 1) {
    return "1fr";
  }
  return `${"max-content ".repeat(count - 1).trimEnd()} minmax(0, 1fr)`;
}

export function buildRuleRowGridTemplateColumns(count: number): string {
  return rowGridTemplateColumns(count);
}

function renderGroupedCell(inner: string, vars: Record<string, string> | undefined): string {
  const innerCols = splitByColumnSeparator(inner);
  if (!innerCols.length) {
    return `<div class="rule-row-col"></div>`;
  }
  const cells = innerCols
    .map((col) => `<div class="rule-row-col">${expandFragmentsInner(col, vars)}</div>`)
    .join("");
  return `<div class="rule-row-col"><div class="rule-row-col-group" style="grid-template-columns: ${rowGridTemplateColumns(innerCols.length)}">${cells}</div></div>`;
}

function renderRowCell(cell: RowCell, vars: Record<string, string> | undefined): string {
  if (cell.kind === "group") {
    return renderGroupedCell(cell.text, vars);
  }
  return renderRuleRowCell(cell.text, vars);
}

function renderRuleRowCell(colText: string, vars: Record<string, string> | undefined): string {
  const { grouped, inner } = unwrapGroupedColumn(colText);
  if (grouped) {
    return renderGroupedCell(inner, vars);
  }
  return `<div class="rule-row-col">${expandFragmentsInner(colText, vars)}</div>`;
}

export function wrapRuleRowHtml(rowHtml: string): string {
  return `<div class="rule-row-wrap">${rowHtml}<span class="rule-row-example-icon" role="img" aria-label="Пример">${CARBON_QUOTES_SVG}</span></div>`;
}

export function emptyRuleRowsVisualHtml(): string {
  const row = `<div class="rule-row" style="grid-template-columns: ${rowGridTemplateColumns(2)}"><div class="rule-row-col"><br></div><div class="rule-row-col"><br></div></div>`;
  return `<div class="rule-rows">${wrapRuleRowHtml(row)}${wrapRuleRowHtml(row)}</div>`;
}

function renderRowsFromLines(lines: string[], vars: Record<string, string> | undefined): string {
  const parsedRows = lines
    .map((line) => parseRowLine(line))
    .filter((cells): cells is RowCell[] => cells !== null);
  if (!parsedRows.length) {
    return "";
  }

  const columnCount = Math.max(...parsedRows.map((cells) => cells.length));
  const parts: string[] = [];
  for (const cells of parsedRows) {
    const htmlCells = cells.map((cell) => renderRowCell(cell, vars));
    while (htmlCells.length < columnCount) {
      htmlCells.push(`<div class="rule-row-col"></div>`);
    }
    parts.push(
      wrapRuleRowHtml(
        `<div class="rule-row" style="grid-template-columns: ${rowGridTemplateColumns(columnCount)}">${htmlCells.join("")}</div>`,
      ),
    );
  }

  return `<div class="rule-rows">${parts.join("")}</div>`;
}

function renderTag(tag: string, classes: string[], innerHtml: string): string {
  if (KNOWN_BLOCK_TAGS.has(tag)) {
    return `<${tag}${classAttr(classes)}>${innerHtml}</${tag}>`;
  }
  return `<span${classAttr(classes)}>${innerHtml}</span>`;
}

function renderVar(name: string, value: string, innerHtml?: string, colorClass?: string): string {
  if (!value) {
    return `<span class="var-missing" data-rule-var="${escapeHtml(name)}">[[${escapeHtml(name)}]]</span>`;
  }
  const classes = ["var", "ar", colorClass].filter(Boolean).join(" ");
  return `<span class="${classes}" data-rule-var="${escapeHtml(name)}" dir="rtl" lang="ar">${
    innerHtml ?? escapeHtml(value)
  }</span>`;
}

/** Plain text (+ vars) for range wrappers; unwrap nested range tags and merge their opts. */
function resolveRangeContent(
  body: string,
  vars: Record<string, string> | undefined,
  opts: CharColorOptions,
): { text: string; varName?: string; opts: CharColorOptions } {
  const trimmed = body.trim();
  const nested = matchBraceTag(trimmed);
  if (nested?.kind === "range" && nested.length === trimmed.length) {
    return resolveRangeContent(nested.body, vars, mergeColorOpts(opts, nested.colorOpts));
  }

  const onlyVar = trimmed.match(/^\[\[([a-zA-Z_][a-zA-Z0-9_]*)\]\]$/);
  if (onlyVar) {
    const name = onlyVar[1]!;
    return { text: vars?.[name] ?? "", varName: name, opts };
  }

  let text = "";
  let index = 0;
  while (index < body.length) {
    const rest = body.slice(index);
    const varMatch = rest.match(VAR_TOKEN_RE);
    if (varMatch) {
      text += vars?.[varMatch[1]!] ?? "";
      index += varMatch[0].length;
      continue;
    }

    const tag = matchBraceTag(rest);
    if (tag?.kind === "range") {
      const inner = resolveRangeContent(tag.body, vars, tag.colorOpts);
      text += inner.text;
      index += tag.length;
      continue;
    }
    if (tag?.kind === "tag") {
      text += resolveRangeContent(tag.body, vars, {}).text;
      index += tag.length;
      continue;
    }

    text += body[index]!;
    index += 1;
  }

  return { text, opts };
}

type PipeRowSegment = { kind: "rows"; lines: string[] } | { kind: "text"; text: string };

function splitIntoPipeRowSegments(text: string): PipeRowSegment[] {
  const lines = text.split("\n");
  const segments: PipeRowSegment[] = [];
  let textBuffer: string[] = [];
  let rowBuffer: string[] = [];

  const flushText = () => {
    if (!textBuffer.length) {
      return;
    }
    segments.push({ kind: "text", text: textBuffer.join("\n") });
    textBuffer = [];
  };

  const flushRows = () => {
    if (!rowBuffer.length) {
      return;
    }
    segments.push({ kind: "rows", lines: [...rowBuffer] });
    rowBuffer = [];
  };

  for (const line of lines) {
    const trimmed = line.trim();
    if (trimmed && splitIntoColumns(trimmed)) {
      flushText();
      rowBuffer.push(trimmed);
      continue;
    }
    flushRows();
    textBuffer.push(line);
  }

  flushText();
  flushRows();
  return segments;
}

function expandTopLevel(text: string, vars: Record<string, string> | undefined): string {
  const segments = splitIntoPipeRowSegments(text);
  if (segments.length === 1 && segments[0]!.kind === "text") {
    return expandFragmentsInner(text, vars);
  }

  return segments
    .map((segment) =>
      segment.kind === "rows"
        ? renderRowsFromLines(segment.lines, vars)
        : expandFragmentsInner(segment.text, vars),
    )
    .join("\n");
}

function expandFragmentsInner(text: string, vars: Record<string, string> | undefined): string {
  const out: string[] = [];
  let index = 0;

  while (index < text.length) {
    const rest = text.slice(index);
    const varMatch = rest.match(VAR_TOKEN_RE);
    const tagToken = matchBraceTag(rest);

    if (varMatch) {
      const name = varMatch[1]!;
      out.push(renderVar(name, vars?.[name] ?? ""));
      index += varMatch[0].length;
      continue;
    }

    if (tagToken?.kind === "range") {
      const resolved = resolveRangeContent(tagToken.body, vars, tagToken.colorOpts);
      const colored = colorizeText(resolved.text, resolved.opts);
      if (resolved.varName !== undefined) {
        out.push(renderVar(resolved.varName, resolved.text, colored));
      } else {
        out.push(colored);
      }
      index += tagToken.length;
      continue;
    }

    if (tagToken?.kind === "tag") {
      const colorFromClasses = tagToken.classes.filter((cls) => COLOR_CLASS_SET.has(cls));
      const bodyTrimmed = tagToken.body.trim();
      const onlyVar = bodyTrimmed.match(/^\[\[([a-zA-Z_][a-zA-Z0-9_]*)\]\]$/);
      if (
        tagToken.tag === "span" &&
        colorFromClasses.length === 1 &&
        tagToken.classes.length === 1 &&
        onlyVar
      ) {
        const varName = onlyVar[1]!;
        out.push(renderVar(varName, vars?.[varName] ?? "", undefined, colorFromClasses[0]));
        index += tagToken.length;
        continue;
      }

      const structuralClasses = tagToken.classes.filter((cls) => !COLOR_CLASS_SET.has(cls));
      const hasNested = tagToken.body.includes("{") || tagToken.body.includes("[[");
      const classes = [...structuralClasses, ...colorFromClasses];

      if (!hasNested) {
        out.push(renderTag(tagToken.tag, classes, escapeHtml(tagToken.body)));
      } else {
        out.push(renderTag(tagToken.tag, classes, expandFragmentsInner(tagToken.body, vars)));
      }
      index += tagToken.length;
      continue;
    }

    const nextSpecial = (() => {
      const positions = ["[[", "{"]
        .map((token) => rest.indexOf(token))
        .filter((pos) => pos >= 0);
      return positions.length ? Math.min(...positions) : -1;
    })();

    if (nextSpecial === -1) {
      out.push(escapeHtml(rest));
      break;
    }

    if (nextSpecial > 0) {
      out.push(escapeHtml(rest.slice(0, nextSpecial)));
      index += nextSpecial;
      continue;
    }

    out.push(escapeHtml(rest[0]!));
    index += 1;
  }

  return out.join("");
}

export function expandNativeMarkup(
  src: string,
  vars: Record<string, string> | undefined,
): string {
  const trimmed = src.trim();
  if (!trimmed) {
    return "";
  }
  return `<div class="rule-markup">${expandTopLevel(trimmed, vars)}</div>`;
}

/** Lesson-only: split rule body into slides. Marker `{step}` on its own line. Catalog shows full `ru`. */
const RULE_STEP_MARKER_RE = /(?:^|\n)\s*\{step\}\s*(?:\n|$)/;

export function splitRuleIntoSteps(ru: string): string[] {
  const trimmed = ru.trim();
  if (!trimmed) {
    return [""];
  }
  if (!RULE_STEP_MARKER_RE.test(trimmed)) {
    return [trimmed];
  }
  const parts = trimmed
    .split(RULE_STEP_MARKER_RE)
    .map((part) => part.trim())
    .filter(Boolean);
  return parts.length ? parts : [trimmed];
}

/** Catalog/modal: all steps merged, `{step}` markers removed. */
export function mergeRuleSteps(ru: string): string {
  return splitRuleIntoSteps(ru).join("\n\n");
}
